import React, { useState, useMemo } from 'react';
import { connect } from "react-redux";
import { useHistory } from 'react-router-dom';
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup'; 
import AppHelper from '../../helpers/AppHelper';

// import { pdf } from '@react-pdf/renderer';
import {
  Container, Paper, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Box, Divider, Grid, Autocomplete, Alert, Snackbar, InputAdornment, CircularProgress
} from '@mui/material';
import { AddCircleOutlined, DeleteOutlined, Download, Search } from '@mui/icons-material';
import UserService from '../../services/UserService';
import AcademyService from '../../services/AcademyService';
// import InvoicePDF from './InvoicePDF';

const mockProducts = [];

// Esquema de Validación
const schema = yup.object().shape({
  docType: yup.string().required(),
  client: yup.object().shape({
    name: yup.string().required('El nombre es obligatorio'),
    document: yup.string()
      .required('El documento es obligatorio')
      .matches(/^[0-9]+$/, 'Solo se permiten números')
      .when('$docType', {
        is: '01',
        then: (schema) => schema.length(11, 'El RUC debe tener exactamente 11 dígitos'),
        otherwise: (schema) => schema.length(8, 'El DNI debe tener exactamente 8 dígitos'),
      }),
    address: yup.string().required('La dirección es obligatoria para facturas'),
  }),
  items: yup.array().of(
    yup.object().shape({
      description: yup.string().required('Descripción requerida'),
      quantity: yup.number().min(1, 'Mínimo 1').required('Requerido').typeError('Debe ser un número'),
      price: yup.number().min(0.01, 'Precio debe ser mayor a 0').required('Requerido').typeError('Debe ser un número'),
    })
  ).min(1, 'Debe haber al menos un ítem'),
});

const TAX_RATE = 0; //WITH IGV: 0.18
const CreateInvoice = (props, { addDocument }) => {
  const { campus } = props;
  const academyService = new AcademyService();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [client, setCLient] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const history = useHistory();

  const { control, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: (values, context, options) => {
      // Inyectamos el valor de useWatch dinámicamente en el validador
      return yupResolver(schema)(values, { ...context, docType: watchDocType }, options);
    },
    defaultValues: {
      docType: '03',
      client: { id: '', name: '', document: '', address: '', paid_at: AppHelper.getLimaDate()},
      items: [{ description: '', quantity: 1, price: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  const watchDocType = useWatch({ control, name: 'docType' });
  const watchItems = useWatch({ control, name: 'items' });

  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 2);
  // 2. Formatear como YYYY-MM-DD para el input nativo
  const minDateString = threeDaysAgo.toISOString().split('T')[0];

  // Cálculo de totales
  const totals = useMemo(() => {
    if (!watchItems) return { subtotal: 0, tax: 0, total: 0 };

    const subtotal = watchItems.reduce((sum, item) => {
      const qty = Number(item?.quantity) || 0;
      const price = Number(item?.price) || 0;
      return sum + (qty * price);
    }, 0);

    const tax = watchDocType === '01' ? subtotal * TAX_RATE : 0;
    const total = subtotal + tax;

    return { subtotal, tax, total };
  }, [watchItems, watchDocType]);


  // Generar y Descargar PDF
  const generatePDF = async (data) => {
    setIsGenerating(true);
    try {
      const docWithTotals = { ...data, totals };

      const newDocumentRecord = {
        serie: `${data.docType === '01' ? 'F001' : 'B001'}`,
        status: '2',
        status_sunat: 'P',
        client: data.client,
        docType: data.docType,
        items: data.items,
        totals: totals,
        campus: campus
      };

      const response = await academyService.createPaymentDoc(newDocumentRecord);

      // addDocument(newDocumentRecord);

    // //   const doc = <InvoicePDF data={docWithTotals} />;
    //   const blob = await pdf(doc).toBlob();
    //   const url = URL.createObjectURL(blob);
    //   const link = document.createElement('a');
    //   link.href = url;
    //   link.download = `${data.docType}_${newDocumentRecord.id}.pdf`;
    //   link.click();
    //   URL.revokeObjectURL(url);
      
      setSnackbar({ open: true, message: `${data.docType} generada exitosamente`, severity: 'success' });
      
      setTimeout(() => history.push('/invoices'), 1000);
    } catch (error) {
      console.error('Error generando PDF:', error);
      setSnackbar({ open: true, message: 'Error al generar el PDF', severity: 'error' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSearchClient =   (docValue) => {
    const doc = String(docValue).trim();
    if (!doc) return;

    setIsSearching(true);
    
    // Simulamos un retraso de red (API)
    setTimeout(async() => {
        const foundClient = await UserService.searchUserByDni(doc);
        setCLient(foundClient.data);
      if (foundClient) {
        setValue('client.name', foundClient.data.fullName, { shouldValidate: true });
        setValue('client.address', foundClient.data.address, { shouldValidate: true });

        setSnackbar({ open: true, message: 'Cliente encontrado y autocompletado', severity: 'info' });
      } else {
        setSnackbar({ open: true, message: 'Cliente no encontrado. Ingrese los datos manualmente.', severity: 'warning' });
      }
      setIsSearching(false);
    }, 800);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {/* <ReceiptLong fontSize="large" color="primary" sx={{ mr: 1 }} /> */}
          <Typography variant="h4" fontWeight="bold" color="primary">
          </Typography>
        </Box>

        <form onSubmit={handleSubmit(generatePDF)}>
          {/* Tipo de documento y datos del cliente */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={2}>
              <Controller
                name="docType"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select fullWidth label="Tipo de Documento">
                    <MenuItem value="03">Boleta de Venta</MenuItem>
                    <MenuItem value="01">Factura</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="client.document"
                control={control}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    label={watchDocType === '01' ? 'RUC (11 dígitos)' : 'DNI (8 dígitos)'}
                    value={value || ''}
                    onChange={(e) => {
                      // Forzar solo números
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      onChange(val);
                    }}
                    onBlur={(e) => {
                      onBlur(); // Notifica a RHF que el campo perdió foco (activa validación)
                      handleSearchClient(value); // Dispara la búsqueda
                    }}
                    error={!!errors.client?.document}
                    helperText={errors.client?.document?.message}
                    disabled={isSearching}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {isSearching ? <CircularProgress size={20} /> : <Search color="action" />}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="client.name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nombre / Razón Social"
                    error={!!errors.client?.name}
                    helperText={errors.client?.name?.message}
                    
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
            <Controller
                name="client.paid_at"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="date"
                    label="Fecha"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: minDateString // Restringe la selección nativa
                    }}
                  />
                )}
              />
            </Grid>
            {/* {watchDocType === '01' && ( */}
              <Grid item xs={12}>
                <Controller
                  name="client.address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Dirección Fiscal"
                      error={!!errors.client?.address}
                      helperText={errors.client?.address?.message}
                    />
                  )}
                />
              </Grid>
            {/* )} */}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>Detalle de Ítems</Typography>
          {typeof errors.items?.message === 'string' && <Alert severity="error" sx={{ mb: 2 }}>{errors.items.message}</Alert>}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ width: '40%' }}>Descripción</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell align="right">Precio Unitario</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, index) => (
                  <TableRow key={field.id}>
                    <TableCell>
                      {/* Autocomplete MEJORADO para texto libre */}
                      <Controller
                        name={`items.${index}.description`}
                        control={control}
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                          <Autocomplete
                            freeSolo
                            options={mockProducts}
                            getOptionLabel={(option) => {
                              // Si es un string (texto libre escrito por el usuario)
                              if (typeof option === 'string') return option;
                              // Si es un objeto (seleccionado del dropdown)
                              return option?.name || '';
                            }}
                            // inputValue y onInputChange aseguran que el texto libre se guarde en tiempo real
                            inputValue={value || ''}
                            onInputChange={(event, newInputValue) => {
                              onChange(newInputValue); // Actualiza React Hook Form
                            }}
                            // onChange detecta cuándo se selecciona una opción del dropdown
                            onChange={(event, selectedOption) => {
                              if (selectedOption && typeof selectedOption === 'object') {
                                // Auto-rellenamos el precio
                                setValue(`items.${index}.price`, selectedOption.price);
                              } else if (!selectedOption) {
                                // Si el usuario borra todo, limpiamos el precio
                                setValue(`items.${index}.price`, 0);
                              }
                            }}
                            onBlur={onBlur}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                inputRef={ref}
                                size="small"
                                variant="standard"
                                placeholder="Escribe o busca..."
                                error={!!errors.items?.[index]?.description}
                                helperText={errors.items?.[index]?.description?.message}
                              />
                            )}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        name={`items.${index}.quantity`}
                        control={control}
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                          <TextField
                            inputRef={ref}
                            variant="standard"
                            type="number"
                            size="small"
                            value={value === 0 ? '' : value}
                            error={!!errors.items?.[index]?.quantity}
                            onBlur={onBlur}
                            onChange={e => {
                              const val = parseInt(e.target.value);
                              onChange(isNaN(val) ? '' : val);
                            }}
                            inputProps={{ min: 1, style: { textAlign: 'right' } }}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        name={`items.${index}.price`}
                        control={control}
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                          <TextField
                            inputRef={ref}
                            variant="standard"
                            type="number"
                            size="small"
                            value={value === 0 ? '' : value}
                            error={!!errors.items?.[index]?.price}
                            onBlur={onBlur}
                            onChange={e => {
                              const val = parseFloat(e.target.value);
                              onChange(isNaN(val) ? '' : val);
                            }}
                            inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1">
                        S/ {((Number(watchItems?.[index]?.quantity) || 0) * (Number(watchItems?.[index]?.price) || 0)).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        onClick={() => remove(index)}
                        disabled={fields.length === 1}
                      >
                        <DeleteOutlined />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2 }}>
            <Button
              startIcon={<AddCircleOutlined />}
              onClick={() => append({ description: '', quantity: 1, price: 0 })}
              variant="outlined"
              size="small"
            >
              Añadir Ítem
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ width: 300 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Subtotal:</Typography>
                <Typography>S/ {totals.subtotal.toFixed(2)}</Typography>
              </Box>
              {watchDocType === 'FACTURA' && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>IGV ({(TAX_RATE * 100).toFixed(0)}%):</Typography>
                  <Typography>S/ {totals.tax.toFixed(2)}</Typography>
                </Box>
              )}
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="h6" fontWeight="bold">TOTAL:</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  S/ {totals.total.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" color="inherit" onClick={() => history.push('/invoices')}>
              Volver al Listado
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              size="large" 
              startIcon={<Download />}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generando PDF...' : `Generar ${watchDocType === '01' ? 'Factura' : 'Boleta'}`}
            </Button>
          </Box>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

const mapStateToProps = state => {
  return {
    user: state.user,
    campus: state.campus,
  };
};

export default connect(mapStateToProps)(CreateInvoice);